/** Turning two places, two map clicks or a GPX file into a 1 Hz track. */
import { parseGpx, synthesizeTrack, type TrackPoint } from '@roadtrip/core';

export interface LatLon {
  lat: number;
  lon: number;
}

export interface Preset {
  name: string;
  waypoints: LatLon[];
  /** A bundled snapshot with landmarks already generated, if one ships with the page. */
  snapshot?: string;
}

export const PRESETS: Preset[] = [
  {
    name: 'I-84 Columbia Gorge · Troutdale to Cascade Locks',
    // Troutdale -> Corbett -> Multnomah Falls -> Bonneville -> Cascade Locks, same as npm run route.
    waypoints: [
      { lat: 45.539, lon: -122.387 },
      { lat: 45.5495, lon: -122.245 },
      { lat: 45.576, lon: -122.116 },
      { lat: 45.625, lon: -121.98 },
      { lat: 45.67, lon: -121.89 },
    ],
    snapshot: 'snapshots/gorge-i84.json',
  },
  { name: 'I-84 · Hood River to The Dalles', waypoints: [{ lat: 45.708, lon: -121.514 }, { lat: 45.598, lon: -121.187 }] },
  { name: 'US-26 · Sandy to Government Camp', waypoints: [{ lat: 45.397, lon: -122.261 }, { lat: 45.303, lon: -121.755 }] },
  { name: 'US-101 · Cannon Beach to Tillamook', waypoints: [{ lat: 45.891, lon: -123.961 }, { lat: 45.456, lon: -123.843 }] },
];

const UA_NOTE = 'roadtrip-guide route lab';

/** Nominatim, one hit. Its usage policy allows light interactive use with a referer, which the browser sends. */
export async function geocode(q: string): Promise<LatLon & { label: string }> {
  const url = `https://nominatim.openstreetmap.org/search?${new URLSearchParams({ q, format: 'json', limit: '1' })}`;
  const res = await fetch(url, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`geocoder ${res.status}`);
  const hits = (await res.json()) as { lat: string; lon: string; display_name: string }[];
  const h = hits[0];
  if (!h) throw new Error(`nowhere called "${q}" (${UA_NOTE})`);
  return { lat: Number(h.lat), lon: Number(h.lon), label: h.display_name.split(',').slice(0, 2).join(',') };
}

/** Road geometry from the public OSRM server, the same call tools/route.ts makes. */
export async function osrmShape(waypoints: LatLon[]): Promise<{ shape: LatLon[]; distanceM: number }> {
  const coords = waypoints.map((w) => `${w.lon},${w.lat}`).join(';');
  const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`);
  if (!res.ok) throw new Error(`OSRM ${res.status}`);
  const j = (await res.json()) as { code: string; routes?: { distance: number; geometry: { coordinates: [number, number][] } }[] };
  const r = j.routes?.[0];
  if (j.code !== 'Ok' || !r) throw new Error(`OSRM: ${j.code}`);
  return { shape: r.geometry.coordinates.map(([lon, lat]) => ({ lat, lon })), distanceM: r.distance };
}

export function trackFromShape(shape: LatLon[], speedMps: number): TrackPoint[] {
  return synthesizeTrack(shape, { speedMps });
}

export async function trackFromGpxFile(file: File): Promise<TrackPoint[]> {
  const t = parseGpx(await file.text());
  if (t.length < 2) throw new Error('no track points in that GPX');
  return t;
}
